import React, { useContext } from 'react';
import { Typography, Spinner, FlexBox } from '@vp/swan';
import { useTranslations } from '@vp/om-translations';
import { aggregateTaxAwarePrices } from '@vp/om-tax-display';
import { TaxAwarePriceRow } from '@vp/component-library';
import { useSelector } from 'react-redux';
import { pricingSelectors } from '../../state/pricing';
import useSiteData from '../../hooks/useSiteData';
import { TaxDisplayContext } from '../shared/taxDisplay/TaxDisplayContext';
import { cartItemSelectors } from '../../state/cartItems';
import IfDebug from '../debugTooling/IfDebug';
import { DebugTaxInfo } from '../debugTooling/DebugTaxInfo';

export function ItemPricingSummary({ cartItemId }) {
  const [cartMessages, pricingErrorMessages, templateBillingMessages] =
    useTranslations([
      'cartMessages',
      'pricingErrorMessages',
      'templateBillingMessages',
    ]);
  const { locale } = useSiteData();

  const pricingData = useSelector(state =>
    pricingSelectors.selectPricingDataByItemId(state.pricing, cartItemId)
  );
  const itemUpdateInProgress = useSelector(state =>
    cartItemSelectors.selectItemUpdateInProgressByItemId(
      state.cartItems,
      cartItemId
    )
  );
  const templatePricingData = useSelector(state =>
    pricingSelectors.selectCartTemplatePricingByItemId(
      state.pricing,
      cartItemId
    )
  );

  const {
    clusivitySeparatedItemPrices,
    clusivitySeparatedTemplatePrices,
    taxDisplayContext,
  } = useContext(TaxDisplayContext);

  const aggregatedItemPrice = aggregateTaxAwarePrices(
    clusivitySeparatedItemPrices[cartItemId],
    'item',
    'detailed',
    [],
    taxDisplayContext
  );

  const aggregatedTemplatePrice = clusivitySeparatedTemplatePrices[cartItemId]
    ? aggregateTaxAwarePrices(
        clusivitySeparatedTemplatePrices[cartItemId],
        'item',
        'detailed',
        [],
        taxDisplayContext
      )
    : {};

  // pull the unit and currency off of the item's pricing data
  const { unit, currency, pricingStatusKey, pricingValid } = pricingData;
  const pricingBlocks = [];

  if (!templatePricingData) {
    pricingBlocks.push({
      ...aggregatedItemPrice,
      label: cartMessages.itemTotal,
      unit,
      currency,
      pricingStatusKey,
      pricingValid,
    });
  } else if (
    aggregatedItemPrice.taxAwareListCents > 0 ||
    aggregatedItemPrice.taxAwareDiscountedCents > 0
  ) {
    pricingBlocks.push({
      ...aggregatedItemPrice,
      label:
        templateBillingMessages[
          `itemPriceLabel:${templatePricingData.templateBillingDisplayType}`
        ],
      unit,
      currency,
    });
  }

  // pull the template currency off of the template pricing data (if available)
  const {
    currency: templateCurrency,
    pricingStatusKey: templatePricingStatusKey,
    pricingValid: templatePricingValid,
  } = templatePricingData || {};

  // only build a template price display if pricing values are present
  if (
    aggregatedTemplatePrice &&
    !!aggregatedTemplatePrice.taxAwareListCents &&
    !!aggregatedTemplatePrice.taxAwareDiscountedCents &&
    !!templateCurrency
  ) {
    pricingBlocks.push({
      ...aggregatedTemplatePrice,
      label:
        templateBillingMessages[
          `templatePriceLabel:${templatePricingData.templateBillingDisplayType}`
        ],
      unit: templateBillingMessages[
        `templatePriceUnit:${templatePricingData.templateBillingDisplayType}`
      ],
      currency: templateCurrency,
      pricingStatusKey: templatePricingStatusKey,
      pricingValid: templatePricingValid,
    });
  }

  return pricingBlocks.map((pricingBlock, index) => {
    const pricingIssueKey = pricingBlock.pricingValid
      ? undefined
      : pricingBlock.pricingStatusKey;

    const pricingIssue = pricingIssueKey
      ? pricingErrorMessages[pricingIssueKey]
      : undefined;

    if (pricingIssue) {
      return (
        <Typography component="label" color="alert" key={index}>
          {pricingIssue} {cartMessages.pricingIssue}
        </Typography>
      );
    }

    return itemUpdateInProgress ? (
      <Spinner
        accessibleText={cartMessages.loading}
        aria-busy="true"
        aria-live="polite"
        role="status"
        key={index}
      />
    ) : (
      <React.Fragment key={index}>
        <TaxAwarePriceRow
          rowLabel={pricingBlock.label}
          labelFormat={{
            fontSize: 2,
          }}
          priceFormat={{
            fontWeight: 'bold',
          }}
          fractionDigits={pricingBlock.fractionDigits}
          listCents={pricingBlock.taxAwareListCents}
          currencyCode={pricingBlock.currency}
          locale={locale}
          discountedCents={pricingBlock.taxAwareDiscountedCents}
          unit={pricingBlock.unit}
          taxes={[
            ...pricingBlock.includedTaxes.map(tax => ({
              ...tax,
              labelFormat: { textColor: 'black' },
            })),
            ...pricingBlock.excludedTaxes.map(tax => ({
              ...tax,
              labelFormat: { textColor: 'black' },
            })),
          ]}
        />
        <IfDebug>
          <FlexBox justifyContent="right">
            <DebugTaxInfo
              aggregatedTaxAwarePrice={aggregatedItemPrice}
              header="Item"
            />
          </FlexBox>
        </IfDebug>
      </React.Fragment>
    );
  });
}
